import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './App.css';

// Lazy load pages for code splitting
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const OtpPage = React.lazy(() => import('./pages/OtpPage'));
const PasswordSetupPage = React.lazy(() => import('./pages/PasswordSetupPage'));
const TermsAndConditionsPage = React.lazy(() => import('./pages/TermsAndConditionsPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const OffersPage = React.lazy(() => import('./pages/OffersPage'));
const OfferDetailsPage = React.lazy(() => import('./pages/OfferDetailsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const CardSetListPage = React.lazy(() => import('./pages/CardSetListPage'));
const CardSetDetailPage = React.lazy(() => import('./pages/CardSetDetailPage'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const OnboardingJourneyPage = React.lazy(() => import('./pages/OnboardingJourneyPage'));
const NotificationListPage = React.lazy(() => import('./pages/NotificationListPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));

// Loading fallback component
function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          display: 'inline-block',
          width: '2rem',
          height: '2rem',
          border: '3px solid #007bff',
          borderRight: '3px solid transparent',
          borderRadius: '50%',
          animation: 'spin 0.75s linear infinite'
        }} />
        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading...</p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function App() {
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated || false);

  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/otp" element={<OtpPage />} />
          <Route path="/setup-password" element={<PasswordSetupPage />} />
          <Route
            path="/terms-and-conditions"
            element={isAuthenticated ? <TermsAndConditionsPage /> : <Navigate to="/login" replace />}
          />

          {/* Protected Routes */}
          <Route
            path="/home"
            element={isAuthenticated ? <HomePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/offers"
            element={isAuthenticated ? <OffersPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/offers/:offerId"
            element={isAuthenticated ? <OfferDetailsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile"
            element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/cardsets"
            element={isAuthenticated ? <CardSetListPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/cardsets/:cardSetId"
            element={isAuthenticated ? <CardSetDetailPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/documents"
            element={isAuthenticated ? <DocumentsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/journey"
            element={isAuthenticated ? <OnboardingJourneyPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/notifications"
            element={isAuthenticated ? <NotificationListPage /> : <Navigate to="/login" replace />}
          />

          {/* Info Pages - Public Routes */}
          <Route
            path="/help"
            element={<HelpPage />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicyPage />}
          />
          <Route
            path="/how-it-works"
            element={<HowItWorksPage />}
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/home" : "/login"} replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
